import {
    LeaderboardGasPriceOptionEnum,
    LeaderboardMarketPriceOptionEnum,
    LeaderboardOptionTypeEnum,
    AncillaryOptionEnum,
    BMBreakdownOptionEnum,
    NonDeliverySplitOutOptionEnum,
} from '@/enums/powerMarkets/LeaderboardEnums.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';

export interface ILeaderboardModalOptions {
    accountForAvailabilityInNormalisation: boolean;
    marketPrice: LeaderboardMarketPriceOptionEnum;
    gasPrice: LeaderboardGasPriceOptionEnum;
    includeColocation: boolean;
    ancillaryRevenueType: AncillaryOptionEnum;
    bmProfitOptions: Array<BMBreakdownOptionEnum>;
    bmRevenueOptions: Array<BMBreakdownOptionEnum>;
    groupDxProducts: boolean;
    groupDxDirections: boolean;
    includeBessInfo: boolean;
    includeCapacityMarket: boolean;
    includeWholesaleSplit: boolean;
    nonDeliverySplitOut: NonDeliverySplitOutOptionEnum;
    includeImbalance: boolean;
    includeEstimatedChargingCost: boolean;
    chargingCostAssumption: string;
    chargingCostPrice: string;
    includeFpnFlagOffWholesale: boolean;
    includeWholesaleRevenueOnly: boolean;
    reservePenaltySplitOut: string;
}

export interface ILeaderboardUserOptions extends ILeaderboardModalOptions {
    revenueType: string;
    leaderboardType: LeaderboardOptionTypeEnum;
    includeMultiAssetBMUs: boolean;
    fuels: Array<string>;
    ownerFuels: Array<string>;
    expandLeaderboard: boolean;
    orderingOption: IIdName;
    selectedGranularity: string;
    selectedDateModel: IAdvancedDatePickerModel;
    /** Optional second calculation config; when set, table shows spread/difference between main and comparison. */
    comparisonOptions?: ILeaderboardModalOptions | null;
}

export interface ILeaderboardSettings {
    userOptions: ILeaderboardUserOptions;
}

/** Payload for updating leaderboard options (either options only or options + comparison). */
export type ILeaderboardOptionsUpdatePayload =
    | ILeaderboardModalOptions
    | { options: ILeaderboardModalOptions; comparisonOptions: ILeaderboardModalOptions | null };

export function createDefaultLeaderboardDateModel (): IAdvancedDatePickerModel {
    return {
        dateRange: {
            from: { type: 'dynamic', value: '0' },
            to: { type: 'dynamic', value: '0' },
        },
        dateType: 'variable',
        daysBefore: 0,
        daysAfter: 0,
    };
}

export const DEFAULT_LEADERBOARD_MODAL_OPTIONS: ILeaderboardModalOptions = {
    accountForAvailabilityInNormalisation: true,
    marketPrice: LeaderboardMarketPriceOptionEnum.WeightedAverageDayAheadPrice,
    gasPrice: LeaderboardGasPriceOptionEnum.DayAheadForward,
    includeColocation: true,
    ancillaryRevenueType: AncillaryOptionEnum.ByProduct,
    bmProfitOptions: [],
    bmRevenueOptions: [],
    groupDxProducts: false,
    groupDxDirections: false,
    includeBessInfo: true,
    includeCapacityMarket: true,
    includeWholesaleSplit: false,
    nonDeliverySplitOut: NonDeliverySplitOutOptionEnum.Show,
    includeImbalance: true,
    includeEstimatedChargingCost: false,
    chargingCostAssumption: 'PreviousEFABlock',
    chargingCostPrice: 'IntradayPrice',
    includeFpnFlagOffWholesale: false,
    includeWholesaleRevenueOnly: false,
    reservePenaltySplitOut: 'Show',
};

export const DEFAULT_LEADERBOARD_SETTINGS: ILeaderboardSettings = {
    userOptions: {
        ...DEFAULT_LEADERBOARD_MODAL_OPTIONS,
        revenueType: 'PoundPerKwPerYear',
        leaderboardType: LeaderboardOptionTypeEnum.Plant,
        includeMultiAssetBMUs: true,
        fuels: [],
        ownerFuels: [],
        orderingOption: { id: 'name', name: 'Alphabetical' },
        expandLeaderboard: false,
        selectedGranularity: 'Day',
        selectedDateModel: createDefaultLeaderboardDateModel(),
    },
};
